import { RefObject, useEffect } from 'react'

// Shared by both inbox hosts (OrderFeedback's Feedback tab, RecoveryAI's
// Escalations tab). Confirmed against the Sketch prototype (v82): opening the
// inbox scrolls the page so the title (and the host page's own nav chrome)
// leave the viewport, with only the description left above the tab bar. The
// inbox (HostFlexShell / useViewportTopOffset, docs/06-ux-rules.md "Height
// model") sizes itself from wherever it mounts, so this directly gives it
// more vertical room. `anchorRef` is the host's PageHeader description row
// (`descriptionRef`, focusable via `tabIndex={-1}`).
//
// One scrollIntoView on activation isn't enough: the inbox's real content
// (RTK Query fetch, InboxLayout) mounts and grows the page AFTER this first
// run, past its loading skeleton — the same "measure races async content"
// problem useViewportTopOffset solves. So every ancestor up to body is
// observed (body alone is height-locked to the viewport and never resizes)
// and the page re-aligns once one of them does. One-shot on purpose: an
// observer left running would keep yanking a viewer who scrolled away right
// after landing back to this position.
export function useScrollInboxIntoView(
  anchorRef: RefObject<HTMLElement>,
  active: boolean
) {
  useEffect(() => {
    if (!active) return undefined
    const node = anchorRef.current
    if (!node) return undefined

    // scrollIntoView moves the viewport only, so focus is moved explicitly or
    // keyboard/switch-control users are stranded on the off-screen tab.
    // `preventScroll` stops the browser re-scrolling the freshly focused node
    // to its own preferred position, which would fight the scroll above.
    const align = () => {
      node.scrollIntoView({ block: 'start' })
      node.focus({ preventScroll: true })
    }

    align()

    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(() => {
      align()
      observer.disconnect()
    })
    for (
      let ancestor: HTMLElement | null = node.parentElement;
      ancestor;
      ancestor = ancestor.parentElement
    ) {
      observer.observe(ancestor)
    }
    observer.observe(document.body)

    return () => observer.disconnect()
  }, [anchorRef, active])
}
